import { useMemo, useState } from 'react'
import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useSearchParams } from 'react-router-dom'
import { Mark } from '@skin/Mark'
import { meta } from '@skin/meta'
import { ConnectChecklist } from './components/ConnectChecklist'
import { OrgSwitcher } from './components/OrgSwitcher'
import { QualityNav } from './components/QualityNav'
import { SidebarFoot } from './components/SidebarFoot'
import { useLandscape } from './hooks/useLandscape'
import { useSession } from './hooks/useSession'
import { useTestify } from './hooks/useTestify'
import { ChangePassword } from './pages/Account'
import { Dashboard } from './pages/Dashboard'
import { ContactManager } from './pages/ContactManager'
import { Landscape } from './pages/Landscape'
import { Login } from './pages/Login'
import { Mailbox } from './pages/Mailbox'
import { NotFound } from './pages/NotFound'
import { Voice } from './pages/Voice'
import { JobPage } from './pages/Jobs'
import { LeadPage, Leads, Opportunities } from './pages/Deals'
import { Contacts } from './pages/Contacts'
import { ContactCardPage } from './pages/Contacts/Card'
import { Outreach } from './pages/Outreach'
import { Outbox } from './pages/Outbox'
import { Workbench } from './pages/Workbench'
import { ErrorBoundary } from './components/ErrorBoundary'
import { CraftingSessionsNav } from './components/CraftingSessionsNav'
import { Telemetry } from './components/Telemetry'
import { ContactsViewsNav } from './components/ContactsViewsNav'
import { Toasts } from './components/Toasts'
import { Accounts, AccountPlanPage } from './pages/Accounts'
import { Playbook } from './pages/Playbook'
import { Expand } from './pages/Expand'
import { ValuePropositionWorkspace } from './pages/ValueProposition'
import { Weekly } from './pages/Weekly'
import { Improve } from './pages/Improve'
import { Meetings } from './pages/Meetings'
import { Notes } from './pages/Notes'
import { Transcripts } from './pages/Transcripts'
import { TranscriptPage } from './pages/Transcripts/TranscriptPage'
import { Connectors } from './pages/Connectors'
import { SnowflakeConnector } from './pages/Connectors/Snowflake'
import { Onboarding, OnboardingReview } from './pages/Onboarding'
import { Consent } from './pages/Consent'
import { inSettings, readSettingsOpen, splitNav, writeSettingsOpen } from './lib/nav'
import { routeScope } from './lib/orgs'
import { Quality } from './pages/Quality'
import { Settings } from './pages/Settings'

// The shell. The Dashboard is home, with the Contact Manager and the Crafting beside it: the three screens the app IS
// (owner, 2026-10-02). Every other screen exists only once the system that feeds it is connected — the API says which,
// the navigation lists only those under System Settings, and a locked route is a 404, never a 403 (ADR 0003 §4). No
// brand string or hex literal lives here — see src/skin/.
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* A client's IT administrator lands here after approving Scout for their tenant. They have no
          Scout account, so this route must sit OUTSIDE RequireSession — gating it would bounce the one
          person whose approval we are asking for to a sign-in screen they can never get past. */}
      <Route path="/consent" element={<Consent />} />
      <Route element={<RequireSession />}>
        {/* Your own account. Inside the session gate and OUTSIDE the feature gates and the shell:
            everybody has a password, nobody's tenant unlocks it, and somebody held here by a
            temporary password cannot load a sidebar the API is refusing to fill. */}
        <Route path="/account/password" element={<ChangePassword />} />
        <Route element={<Shell />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/landscape" element={<Landscape />} />
          <Route path="/mailbox" element={<Mailbox />} />
          <Route path="/jobs/:jobId" element={<JobPage />} />
          <Route path="/voice" element={<Gated id="voice"><Voice /></Gated>} />
          <Route path="/outreach" element={<Gated id="outreach"><Outreach /></Gated>} />
          <Route path="/outbox" element={<Gated id="outbox"><Outbox /></Gated>} />
          <Route path="/workbench/:companyId?" element={<Gated id="workbench"><Workbench /></Gated>} />
          <Route path="/leads" element={<Gated id="leads"><Leads /></Gated>} />
          <Route path="/leads/:engagementId" element={<Gated id="leads"><LeadPage tab="leads" /></Gated>} />
          <Route path="/opportunities" element={<Gated id="opportunities"><Opportunities /></Gated>} />
          <Route path="/opportunities/:engagementId" element={<Gated id="opportunities"><LeadPage tab="opportunities" /></Gated>} />
          {/* owner, 2026-10-07: everyone as a list is the default; the deck (one tile at a time) is a step away */}
          <Route path="/contacts" element={<Gated id="contact-manager"><ContactsScreen /></Gated>} />
          <Route path="/contacts/list" element={<Gated id="contacts"><Contacts /></Gated>} />
          <Route path="/contacts/:personId" element={<Gated id="contacts"><ContactCardPage /></Gated>} />
          <Route path="/accounts" element={<Gated id="accounts"><Accounts /></Gated>} />
          <Route path="/accounts/:companyId" element={<Gated id="accounts"><AccountPlanPage /></Gated>} />
          <Route path="/playbook" element={<Gated id="playbook"><Playbook /></Gated>} />
          <Route path="/expand" element={<Gated id="expand"><Expand /></Gated>} />
          <Route path="/expand/:companyId/:line" element={<Gated id="expand"><ValuePropositionWorkspace /></Gated>} />
          <Route path="/weekly" element={<Gated id="weekly"><Weekly /></Gated>} />
          <Route path="/improve" element={<Gated id="improve"><Improve /></Gated>} />
          <Route path="/meetings" element={<Meetings />} />
          <Route path="/notes" element={<Notes />} />
          <Route path="/transcripts" element={<Gated id="transcripts"><Transcripts /></Gated>} />
          <Route path="/transcripts/:transcriptId" element={<Gated id="transcripts"><TranscriptPage /></Gated>} />
          <Route path="/connectors" element={<Gated id="connectors"><Connectors /></Gated>} />
          <Route path="/connectors/snowflake" element={<Gated id="connectors"><SnowflakeConnector /></Gated>} />
          <Route path="/onboarding" element={<Gated id="onboarding"><Onboarding /></Gated>} />
          <Route path="/onboarding/:batchId" element={<Gated id="onboarding"><OnboardingReview /></Gated>} />
          <Route path="/settings" element={<Gated id="settings"><Settings /></Gated>} />
          <Route path="/quality" element={<Quality view="board" />} />
          <Route path="/quality/stats" element={<Quality view="dashboard" />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  )
}

function RequireSession() {
  const { status, user } = useSession()
  const location = useLocation()
  if (status === 'loading') return <p className="muted centered">Loading…</p>
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  // A password an admin minted is a delivery mechanism, not a secret: until they choose their own,
  // this is the only screen. The API refuses everything else regardless — this just stops the person
  // walking into a wall of refusals to find that out.
  if (user?.mustChangePassword && location.pathname !== '/account/password')
    return <Navigate to="/account/password" replace />
  return <Outlet />
}

/** A screen that does not exist for this person until its system is connected. */
/** The Contacts screen: the list of everyone by default; the swipe deck when asked for (?view=cards). */
function ContactsScreen() {
  const [params] = useSearchParams()
  return params.get('view') === 'cards' ? <ContactManager /> : <Contacts />
}

function Gated({ id, children }: { id: string; children: React.ReactNode }) {
  const landscape = useLandscape()
  if (landscape.isPending) return <p className="muted centered">Loading…</p>
  const unlocked = landscape.data?.screens.some((s) => s.id === id && s.unlocked) ?? false
  return unlocked ? <>{children}</> : <NotFound />
}

function Shell() {
  const { user, signOut } = useSession()
  const location = useLocation()
  const landscape = useLandscape()
  useTestify(user)   // the QA widget, armed for this person once they are in (a guest: a 404 changes nothing)
  const nav = ({ isActive }: { isActive: boolean }) => (isActive ? 'nav-item active' : 'nav-item')
  const systems = landscape.data?.systems ?? []
  // The menu: the three Home screens as the primary items, everything else under System Settings in the old groups
  // (lib/nav.ts). Connect is the checklist of every connection point, in roster order, straight from the API.
  const split = useMemo(() => splitNav(landscape.data?.screens ?? [], systems), [landscape.data, systems])
  // System Settings is open when the person opened it, or when the page they are on lives there
  const [opened, setOpened] = useState(() => readSettingsOpen())
  const here = inSettings(split, systems, location.pathname)
  const showSettings = opened || here
  const toggleSettings = () => { const next = !showSettings; setOpened(next); writeSettingsOpen(next) }
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <Mark size={28} />
          <span className="brand-name">{meta.productName}</span>
        </div>
        {/* Whose pipeline this is. Every list below answers for the org named here — except on the
            screens that are only ever yours (your mailbox, your voice, a job you started, your
            account), where the control says so instead of implying an org it cannot keep. */}
        <OrgSwitcher personal={routeScope(location.pathname) === 'personal'} />
        <nav>
          <div className="nav-primary">
            {split.primary.map((it) => (
              <div key={it.route}>
                <NavLink to={it.route} end={it.route === '/'} className={nav}>
                  {it.title}
                </NavLink>
                {/* each opportunity being crafted, under Crafting: a spinner while it is worked, a dot when an answer waits (owner, 2026-10-07) */}
                {it.route === '/workbench' && <CraftingSessionsNav />}
                {/* the swipe deck, under Contacts: in the menu as well as behind the button on the list (owner, PL-202: "both") */}
                {it.route === '/contacts' && <ContactsViewsNav />}
              </div>
            ))}
          </div>
          <button type="button" className={`nav-settings-toggle ${showSettings ? 'open' : ''}`} onClick={toggleSettings} aria-expanded={showSettings}>
            System Settings
          </button>
          {showSettings && (
            <div className="nav-settings">
              {split.groups.map(([group, items]) => (
                <div key={group} className="nav-section">
                  <div className="nav-group">{group}</div>
                  {group === 'Connect' && <ConnectChecklist systems={systems} />}
                  {items.map((it) => (
                    <NavLink key={it.route} to={it.route} className={nav}>
                      {it.title}
                    </NavLink>
                  ))}
                </div>
              ))}
              <QualityNav />
            </div>
          )}
        </nav>
        <SidebarFoot
          displayName={user?.displayName}
          from={location.pathname + location.search}
          onSignOut={() => void signOut()}
        />
      </aside>
      <main className="canvas">
        {/* a page that throws while drawing shows one honest screen instead of taking the shell with it; a new route resets it */}
        <ErrorBoundary key={location.pathname} where={location.pathname}>
          <Outlet />
        </ErrorBoundary>
        <Toasts />
        {/* front-end analytics (owner, 2026-10-08): what each person does and sees, kept under their own id for /self-improve */}
        <Telemetry />
      </main>
    </div>
  )
}
